import diplomaMedal from "assets/illustrations/diplomaMedal.svg"
import diplomaRibbon from "assets/illustrations/diplomaRibbon.svg"
import { formatDate } from "format"
import { upperFirst } from "lodash-es"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { optimizeImage, useOptimizeContext } from "ui-tools"
import { withQueryParams } from "utils"
import type { DiplomaPreviewProps } from "../types"

import s from "./styles.module.scss"

export const DiplomaPreviewUnlocked = (props: DiplomaPreviewProps) => {
  return (
    <div className={s.diploma}>
      <div className={s.diploma__section}>
        <img className={s.medal} src={diplomaMedal} alt="Diploma badge" />

        <Ribbon title={props.title} />

        <Owner
          firstName={props.firstName}
          lastName={props.lastName}
          showUserLastName={props.showUserLastName}
        />
      </div>

      <div className={s.diploma__section}>
        <Company
          companyName={props.companyName}
          logo={props.logo}
          showCompanyLogo={props.showCompanyLogo}
          showCompanyName={props.showCompanyName}
        />

        <ProductTitle
          productTitle={props.productTitle}
          showProductTitle={props.showProductTitle}
        />

        <Description
          description={props.description}
          showDiplomaDescription={props.showDiplomaDescription}
        />

        <Dates
          expirationDate={props.expirationDate}
          issueDate={props.issueDate}
          showIssueDate={props.showIssueDate}
        />
      </div>
    </div>
  )
}

type RibbonProps = Pick<DiplomaPreviewProps, "title">
const Ribbon = ({ title }: RibbonProps) => {
  return (
    <div className={s.ribbon}>
      <div className={s.ribbon__text} id="ribbon__text">
        {title}
      </div>
      <img className={s.ribbon__img} src={diplomaRibbon} alt="" />
    </div>
  )
}

type OwnerProps = Pick<
  DiplomaPreviewProps,
  "firstName" | "lastName" | "showUserLastName"
>
const Owner = ({ firstName, lastName, showUserLastName }: OwnerProps) => {
  const { t } = useTranslation()

  return (
    <div className={s.owner}>
      <div className={s.owner__desc}>{t("diploma.CERTIFICATE_AWARDED_TO")}</div>
      <div className={s.owner__name}>
        {firstName} {showUserLastName && lastName}
      </div>
    </div>
  )
}

type CompanyProps = Pick<
  DiplomaPreviewProps,
  "companyName" | "logo" | "showCompanyName" | "showCompanyLogo"
>
const Company = ({
  companyName,
  logo,
  showCompanyName,
  showCompanyLogo,
}: CompanyProps) => {
  const { t } = useTranslation()
  const showCompanyText = showCompanyLogo || showCompanyName

  return (
    <div className={s.company}>
      {showCompanyText && (
        <div className={s.company__text}>{t("diploma.FROM")}</div>
      )}

      {showCompanyLogo && <Logo logo={logo} />}

      {showCompanyName && <div className={s.company__text}>{companyName}</div>}
    </div>
  )
}

const Logo = ({ logo }: { logo: string }) => {
  const context = useOptimizeContext()

  const optimizedLogo = useMemo(() => {
    // We can't optimize the logo if it's base64 & don't need to cache bust it
    const shouldSkipOptimization = !logo.startsWith("http")
    if (shouldSkipOptimization) {
      return logo
    }

    // Forces redownloading image and disregard cache.
    // If not done, company logo won't print on diplomas in Chromium
    // based browsers due to https://issues.chromium.org/issues/41025985
    const srcWithCacheBuster = withQueryParams(logo, { c: Date.now() })

    const { app, optimizeUrl } = context || {}
    if (!app || !optimizeUrl) {
      return srcWithCacheBuster
    }

    // Putting the image through the optimizer not only optimizes it,
    // but also acts as a proxy which handles CORS issues as image
    // can have many different origins
    return optimizeImage({
      app,
      optimizeUrl,
      src: srcWithCacheBuster,
      height: 300,
    })
  }, [context, logo])

  return (
    <img className={s.company__logo} src={optimizedLogo} alt="Company logo" />
  )
}

type ProductTitleProps = Pick<
  DiplomaPreviewProps,
  "productTitle" | "showProductTitle"
>
const ProductTitle = ({
  productTitle,
  showProductTitle,
}: ProductTitleProps) => {
  const { t } = useTranslation()

  if (showProductTitle)
    return (
      <div className={s.product}>
        <div className={s.product__text}>{t("diploma.FOR_COMPLETING")}</div>
        <div className={s.product__title}>{productTitle}</div>
      </div>
    )
}

type DescriptionProps = Pick<
  DiplomaPreviewProps,
  "description" | "showDiplomaDescription"
>
const Description = ({
  description,
  showDiplomaDescription,
}: DescriptionProps) => {
  if (description && showDiplomaDescription)
    return <div className={s.description}>{description}</div>
}

type DatesProps = Pick<
  DiplomaPreviewProps,
  "issueDate" | "expirationDate" | "showIssueDate"
>
const Dates = ({ issueDate, expirationDate, showIssueDate }: DatesProps) => {
  const { i18n, t } = useTranslation()

  return (
    <div className={s.date}>
      {showIssueDate && issueDate && (
        <div className={s.date__issued}>
          {t("diploma.ISSUED")} {formatDeadlineDate(issueDate, i18n.language)}
        </div>
      )}
      {expirationDate && (
        <div className={s.date__expires}>
          {t("diploma.EXPIRES")}{" "}
          {formatDeadlineDate(expirationDate, i18n.language)}
        </div>
      )}
    </div>
  )
}

const formatDeadlineDate = (deadlineDate: string, locale: string) => {
  const formatted = formatDate(deadlineDate, locale, {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  })
  return upperFirst(formatted)
}
